import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import i18n, { getAppText } from 'src/i18n';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';

export interface ManagerDto {
    [key: string]: any;
    id: number;
    referenceYear_Id: number;
    employeeId: number;
    employeeName: string;
    originalManagerType_Id: number;
    manualManagerType_Id: number;
    originalGroupSize_Id: number;
    manualGroupSize_Id: number;
    originalHrLevel_Id: number;
    groupSize: number;
    employeeNumber: string;
}

export interface AutocompleteManagerDto {
    [key: string]: any;
    name: string;
    id: string;
}

export interface ManagerState {
    managers: ManagerDto[];
    manager: ManagerDto;
    gettingManagers: boolean;
    savingManager: boolean;
    gettingManager: boolean;
    archivingManager: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    gettingManagerStatus: 'idle' | 'loading' | 'succeeded' | 'failed';

    error?: string;
    gettingAutoCompleteManagers: boolean;
    gettingAutoCompleteManagerPicture: boolean;
    gettingAutoCompleteManagerStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    gettingAutoCompleteManagerPictureStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    autocompleteManagers?: AutocompleteManagerDto[];
    autocompletedManager?: AutocompleteManagerDto;
    autocompletedManagerPicture: string;
}

const blankManagerDto: ManagerDto = {
    id: 0,
    employeeId: 0,
    employeeName: '',
    referenceYear_Id: 0,
    originalManagerType_Id: 0,
    manualManagerType_Id: 0,
    originalGroupSize_Id: 0,
    manualGroupSize_Id: 0,
    originalHrLevel_Id: 0,
    groupSize: 0,
    employeeNumber: '',
};

const blankAutoCompleteManagerDto: AutocompleteManagerDto = {
    id: '',
    name: '',
};
const initialState: ManagerState = {
    managers: [],
    manager: blankManagerDto,
    savingManager: false,
    gettingManagers: false,
    gettingManager: false,
    archivingManager: false,
    status: 'idle',
    gettingManagerStatus: 'idle',
    error: undefined,
    gettingAutoCompleteManagers: false,
    gettingAutoCompleteManagerPicture: false,
    gettingAutoCompleteManagerStatus: 'idle',
    gettingAutoCompleteManagerPictureStatus: 'idle',
    autocompleteManagers: [],
    autocompletedManagerPicture: '',
};

export const managerSlice = createSlice({
    name: 'managers',
    initialState,
    reducers: {
        clearManagers: (state) => {
            state.managers = [];
            state.manager = blankManagerDto;
            state.autocompleteManagers = [];
            state.autocompletedManager = blankAutoCompleteManagerDto;
            state.autocompletedManagerPicture = '';
        },
        updateManager: (state, param) => {
            const { payload } = param;
            state.manager = payload;
        },
        setAutoCompletedManager: (state, param) => {
            const { payload } = param;
            state.autocompletedManager = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getManagers.pending, (state, action) => {
                state.gettingManagers = true;
                state.status = 'loading';
            })
            .addCase(getManagers.fulfilled, (state, action) => {
                state.gettingManagers = false;
                state.managers = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getManagers.rejected, (state, action) => {
                state.gettingManagers = false;
                state.managers = [];
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getManagerPicture.pending, (state, action) => {
                state.gettingAutoCompleteManagerPicture = true;
                state.gettingAutoCompleteManagerPictureStatus = 'loading';
            })
            .addCase(getManagerPicture.fulfilled, (state, action) => {
                state.gettingAutoCompleteManagerPicture = false;
                if (!action.payload || action.payload === '') {
                    state.autocompletedManagerPicture = '/images/empty-profile.jpg';
                } else {
                    state.autocompletedManagerPicture = action.payload;
                }
                state.gettingAutoCompleteManagerPictureStatus = 'succeeded';
            })
            .addCase(getManagerPicture.rejected, (state, action) => {
                state.gettingAutoCompleteManagerPicture = false;
                state.autocompletedManagerPicture = '/images/empty-profile.jpg';
                state.gettingAutoCompleteManagerPictureStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(getAutoCompleteManager.pending, (state, action) => {
                state.gettingAutoCompleteManagers = true;
                state.gettingAutoCompleteManagerStatus = 'loading';
            })
            .addCase(getAutoCompleteManager.fulfilled, (state, action) => {
                state.gettingAutoCompleteManagers = false;
                state.autocompleteManagers = action.payload;
                state.gettingAutoCompleteManagerStatus = 'succeeded';
            })
            .addCase(getAutoCompleteManager.rejected, (state, action) => {
                state.gettingAutoCompleteManagers = false;
                state.autocompleteManagers = [];
                state.gettingAutoCompleteManagerStatus = 'failed';
                state.error = action.error.message;
            })

            .addCase(synchronizeManagers.pending, (state, action) => {
                state.gettingManagers = true;
                state.status = 'loading';
            })
            .addCase(synchronizeManagers.fulfilled, (state, action) => {
                state.gettingManagers = false;
                state.managers = action.payload;
                state.status = 'succeeded';
            })
            .addCase(synchronizeManagers.rejected, (state, action) => {
                state.gettingManagers = false;
                state.managers = [];
                state.status = 'failed';
                state.error = action.error.message;
            })

            .addCase(getManager.pending, (state, action) => {
                state.gettingManager = true;
                state.gettingManagerStatus = 'loading';
            })
            .addCase(getManager.fulfilled, (state, action) => {
                state.gettingManager = false;
                state.manager = action.payload;
                state.autocompletedManager = { name: action.payload.employeeName, id: action.payload.employeeNumber };
                state.gettingManagerStatus = 'succeeded';
            })
            .addCase(getManager.rejected, (state, action) => {
                state.gettingManager = false;
                state.manager = blankManagerDto;
                state.gettingManagerStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(archiveManager.pending, (state, action) => {
                state.archivingManager = true;
                state.status = 'loading';
            })
            .addCase(archiveManager.fulfilled, (state, action) => {
                state.archivingManager = false;
                state.managers = state.managers.filter((x) => x.id !== action.payload.id);
                state.status = 'succeeded';
            })
            .addCase(archiveManager.rejected, (state, action) => {
                state.archivingManager = false;
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { clearManagers } = managerSlice.actions;
export const { updateManager } = managerSlice.actions;
export const { setAutoCompletedManager } = managerSlice.actions;

export const getManagers = createAsyncThunk('managers/getManagers', async (referenceYearId: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/managers/getManagers/${referenceYearId}`);

    return response.data;
});

export const synchronizeManagers = createAsyncThunk('managers/synchronizeManagers', async (referenceYearId: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/managers/synchronizeManagers/${referenceYearId}`);

    return response.data;
});

export const getManager = createAsyncThunk('managers/getManager', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/managers/${id}`);

    return response.data;
});

export const getAutoCompleteManager = createAsyncThunk('managers/getAutoCompleteManager', async (search: string, thunkAPI) => {
    const response = await axios.get(`/api/synergy/managers/autocomplete/${search}`);

    return response.data;
});

export const getManagerPicture = createAsyncThunk('managers/getManagerPicture', async (username: string, thunkAPI) => {
    const response = await axios.get(`/api/employee/get-picture-by-username/${username}`);

    return response.data;
});

export const archiveManager = createAsyncThunk('managers/archiveManager', async (id: number, thunkAPI) => {
    const response = await axios.post(`/api/synergy/managers/archive/${id}`);
    thunkAPI.dispatch(
        showMessage({
            type: 'info',
            message: getAppText('archivedManager'),
        })
    );
    return response.data;
});

export const saveManager = createAsyncThunk('managers/saveManager', async (managerToSave: ManagerDto, thunkAPI) => {
    if (managerToSave.id === 0) {
        return axios
            .post(`/api/synergy/managers/create`, managerToSave)
            .then((data) => {
                thunkAPI.dispatch(
                    showMessage({
                        type: 'info',
                        message: getAppText('createManager'),
                    })
                );
                return data;
            })
            .catch(async (error) => {
                const message = await error.response.data.text();
                thunkAPI.dispatch(
                    showMessage({
                        type: 'error',
                        message: i18n.t(message),
                    })
                );
            });
    } else {
        const response = await axios.put(`/api/synergy/managers/put`, managerToSave);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('saveManager'),
            })
        );
        return response.data;
    }
});
